import { positionKey } from "../chess/positionKey.ts";
import { teachingMoveFor } from "../lessons/repository.ts";
import type { LessonCourse } from "../lessons/schema.ts";

export type PracticeBranch = {
  id: string;
  name: string;
  san: string;
  opponent: "w" | "b";
  before: string;
  after: string;
  referenceAfter: string;
  referenceSan: string;
  prefix: string[];
  why: string;
  decisions: number;
};

const catalog = new WeakMap<LessonCourse, PracticeBranch[]>();

/** A branch is an opponent decision at a reachable position, not just a SAN or a name. */
export function practiceBranches(course: LessonCourse): PracticeBranch[] {
  const cached = catalog.get(course);
  if (cached) return cached;
  const student = course.side === "white" ? "w" : "b";
  const opponent = student === "w" ? "b" : "w";
  const branches: PracticeBranch[] = [];
  const queue = [{ key: course.rootPositionKey, path: [] as string[] }];
  const seen = new Set<string>();
  while (queue.length) {
    const item = queue.shift()!;
    if (seen.has(item.key)) continue;
    seen.add(item.key);
    const node = course.nodes[item.key];
    if (!node || item.path.length >= 30) continue;
    for (const move of node.moves) {
      const dest = course.nodes[move.nextPositionKey];
      if (!dest || move.role === "mistake" || move.role === "handoff") continue;
      const prefix = [...item.path, move.san];
      queue.push({ key: move.nextPositionKey, path: prefix });
      if (node.fen.split(" ")[1] !== opponent || ["repertoire", "mainline"].includes(move.role))
        continue;
      const name = move.variationName || dest.variationName;
      if (!name || name === course.title || !teachingMoveFor(dest)) continue;
      let current = dest;
      let decisions = 0;
      const walked = new Set<string>();
      while (current && !current.handoff && !walked.has(current.positionKey)) {
        walked.add(current.positionKey);
        const next = teachingMoveFor(current);
        if (!next) break;
        if (current.fen.split(" ")[1] === student) decisions += 1;
        current = course.nodes[next.nextPositionKey];
      }
      if (!decisions) continue;
      branches.push({
        id: `${course.openingId}:${positionKey(node.fen)}:${move.uci}`,
        name,
        san: move.san,
        opponent,
        before: node.fen,
        after: dest.fen,
        prefix,
        referenceAfter: course.nodes[teachingMoveFor(node)?.nextPositionKey || ""]?.fen || node.fen,
        referenceSan: teachingMoveFor(node)?.san || "",
        why: move.explanation,
        decisions,
      });
    }
  }
  catalog.set(course, branches);
  return branches;
}
